import React, { useState } from 'react';
import { CheckIcon, RotateCcwIcon, XIcon } from 'lucide-react';
import { useAppState } from '../../contexts/AppStateContext';
import { useUi } from '../../contexts/UiContext';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyNote } from '../../components/ui/Panel';
import { Button } from '../../components/ui/Button';
import { DutyDots } from '../../components/duties/DutyDots';
import { TaskTemplate } from '../../types/family';
import { DutySlot } from '../../types/shared';
import { currentSlot, dutySlots, isDuty } from '../../utils/duties';
import { namesOf } from '../../utils/members';
import { formatDate } from '../../utils/time';

/** Starš pregleda tedenske zadolžitve: do konca ponedeljka, srede in sobote. */
export function DutyReview() {
  const {
    family
  } = useAppState();
  const {
    openSub
  } = useUi();
  const [slot, setSlot] = useState<DutySlot>(currentSlot());
  const duties = family.modules.hisnaOpravila ? family.templates.filter(isDuty) : [];
  const house = duties.filter(t => t.planKey !== 'soba');
  const rooms = duties.filter(t => t.planKey === 'soba');
  const active = dutySlots.find(s => s.slot === slot);
  return <div className="mx-auto max-w-3xl" data-id="element-1304" data-mp="3p64nne8rst8s">
      <PageHeader title="Pregled zadolžitev" subtitle="Trije pregledi na teden. Cvenkotočke po deležu urejenih; pregled, ki ga ni bilo, se ne šteje." onBack={() => openSub(null)} backLabel="Pregled" data-id="element-1305" data-mp="3p5cu08yqh5l5" />

      <div role="tablist" aria-label="Pregled" className="mb-5 inline-flex rounded-2xl bg-canvas p-1" data-id="element-1306" data-mp="70t4hyay35vl">
        {dutySlots.map(s => <button key={s.slot} role="tab" type="button" aria-selected={slot === s.slot} onClick={() => setSlot(s.slot)} className={`rounded-xl px-4 py-2 text-sm font-bold transition-colors duration-150 ${slot === s.slot ? 'bg-surface shadow-pressSoft' : 'text-muted hover:text-ink'}`} data-id="element-1307" data-mp="bt2pr1147hrj">
            {s.short} {formatDate(s.date)}
          </button>)}
      </div>
      <p className="-mt-3 mb-4 text-xs text-muted" data-id="element-1308" data-mp="3s77nnmi4pog1">Pregled {active?.label}.</p>

      {duties.length === 0 ? <EmptyNote data-id="element-1309" data-mp="aqg1ym1mhzet">V tej družini ni tedenskih zadolžitev.</EmptyNote> : <div className="space-y-8" data-id="element-1310" data-mp="asiwnjdq0gpv">
          {house.length > 0 && <DutyGroup title="Hiša" list={house} slot={slot} data-id="element-1311" data-mp="5z7sjq0ame1n" />}
          {rooms.length > 0 && <DutyGroup title="Sobe" list={rooms} slot={slot} data-id="element-1312" data-mp="jqcuiyr4vif5" />}
        </div>}
    </div>;
}
function DutyGroup({
  title,
  list,
  slot
}: {
  title: string;
  list: TaskTemplate[];
  slot: DutySlot;
}) {
  return <section aria-label={title} data-id="element-1313" data-mp="to94hn8c47bo">
      <h2 className="mb-2 px-1 font-display text-lg" data-id="element-1314" data-mp="tnhf4u5bz5tu">{title}</h2>
      <ul className="divide-y divide-line rounded-3xl border-[1.5px] border-line bg-surface" data-id="element-1315" data-mp="orsdml9r3euw">
        {list.map(t => <DutyReviewRow key={t.id} template={t} slot={slot} data-id="element-1316" data-mp="2vj1b8deytrr7" />)}
      </ul>
    </section>;
}
function DutyReviewRow({
  template,
  slot
}: {
  template: TaskTemplate;
  slot: DutySlot;
}) {
  const {
    family,
    dutyChecksOf,
    dutyReportsOf,
    setDutyCheck,
    setDutyFixed,
    memberById
  } = useAppState();
  const [note, setNote] = useState('');
  const [asking, setAsking] = useState(false);
  const checks = dutyChecksOf(template.id);
  const check = checks.find(c => c.slot === slot);
  const reports = dutyReportsOf(template.id).slice(-2);
  const room = template.planKey === 'soba';
  return <li className="p-4" data-id="element-1317" data-mp="n80k0moou2ki">
      <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-1318" data-mp="rax64ndfsgt7">
        <div className="min-w-0" data-id="element-1319" data-mp="rc8bgrmj5wmf">
          <p className="font-bold" data-id="element-1320" data-mp="dbdxzukml5gw">{room ? `Soba: ${namesOf(family, template.assigneeIds)}` : template.title}</p>
          <p className="text-xs text-muted" data-id="element-1321" data-mp="i9p9e9qkjo18">
            {room ? '' : `${namesOf(family, template.assigneeIds)} · `}
            {template.baseValue} cvenkotočk
          </p>
          {reports.map(r => <p key={r.id} className="mt-1 text-xs text-info-ink" data-id="element-1322" data-mp="2coi4m7njicsn">
              {memberById(r.memberId)?.name}, {formatDate(r.date)}: {r.text}
            </p>)}
        </div>
        <DutyDots checks={checks} data-id="element-1323" data-mp="7me2kd53fen2" />
      </div>

      {check?.result === 'ni-urejeno' && <div className="mt-3 flex flex-wrap items-center gap-2 rounded-2xl bg-accent-soft px-3 py-2 text-sm text-accent-ink" data-id="element-1324" data-mp="3l29fxn0gba4">
          <span className="min-w-0 flex-1" data-id="element-1325" data-mp="3uqk6h0kg50n0">
            <b data-id="element-1326" data-mp="3uty8g7p777tx">Ni urejeno</b>
            {check.note ? ` · ${check.note}` : ''}
            {check.fixed ? ' · popravek potrjen' : ''}
          </span>
          <Button size="sm" variant={check.fixed ? 'ghost' : 'success'} onClick={() => setDutyFixed(template.id, slot, !check.fixed)} data-id="element-1327" data-mp="3l0r5ethxk3x0">
            {check.fixed ? 'Prekliči popravek' : 'Popravljeno'}
          </Button>
        </div>}

      {asking ? <form className="mt-3 flex flex-col gap-2 sm:flex-row" onSubmit={e => {
      e.preventDefault();
      setDutyCheck(template.id, slot, 'ni-urejeno', note.trim() || undefined);
      setAsking(false);
      setNote('');
    }} data-id="element-1328" data-mp="37218bdv4248n">
          <input className="field flex-1" autoFocus aria-label="Kaj ni urejeno?" placeholder={room ? 'npr. Pod posteljo živi nogavica.' : 'Kaj ni urejeno? (neobvezno)'} value={note} onChange={e => setNote(e.target.value)} data-id="element-1329" data-mp="1mk05eh4uz6zg" />
          <Button type="submit" variant="secondary" data-id="element-1330" data-mp="yunzbq4tvf1d">
            <RotateCcwIcon className="h-4 w-4" aria-hidden="true" data-id="element-1331" data-mp="yvz4nudx8uul" /> Pošlji v popravek
          </Button>
          <Button type="button" variant="ghost" onClick={() => setAsking(false)} data-id="element-1332" data-mp="13tqtgfj9s0uk">
            Prekliči
          </Button>
        </form> : <div className="mt-3 flex flex-wrap gap-2" data-id="element-1333" data-mp="1clkh0lx7ubtn">
          <Button size="sm" variant={check?.result === 'urejeno' ? 'success' : 'secondary'} aria-pressed={check?.result === 'urejeno'} onClick={() => setDutyCheck(template.id, slot, 'urejeno')} data-id="element-1334" data-mp="1cn3u63nks6r7">
            <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1335" data-mp="k3otunzah6m0" /> Urejeno
          </Button>
          <Button size="sm" variant="secondary" aria-pressed={check?.result === 'ni-urejeno'} onClick={() => setAsking(true)} data-id="element-1336" data-mp="1neujkjgvilv2">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-1337" data-mp="1ifhya35nhmul" /> Ni urejeno
          </Button>
          {check && <Button size="sm" variant="ghost" onClick={() => setDutyCheck(template.id, slot, null)} data-id="element-1338" data-mp="1s75hxhvytzli">
              Razveljavi
            </Button>}
        </div>}
    </li>;
}